import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Building2, RefreshCw, Download, Settings, Trash2, Search, X, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const csv = (filename, rows) => {
    const escape = v => v === null || v === undefined ? '' : (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
    const text = rows.map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + text], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric' }) : '—';

/**
 * Locations (Operators) admin tab.
 * - No distributors, no credit rate
 * - Create operator by phone+name+password
 * - Per-operator feature toggles (Sportsbook / Casino / Lottery / etc.)
 */
export const StaffAdminTab = () => {
    const [operators, setOperators] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);

    // Create form
    const [form, setForm] = useState({ name: '', phone: '', password: '' });

    // Feature editor dialog
    const [editing, setEditing] = useState(null);          // { operator_id, operator_name, features, catalog }
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/operators`);
            setOperators(data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load locations');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const submitOperator = async (e) => {
        e.preventDefault();
        const name = form.name.trim();
        const phone = form.phone.trim();
        const password = form.password;
        if (!name || !phone || password.length < 4) {
            toast.error('Name, phone, and password (4+ chars) required');
            return;
        }
        setCreating(true);
        try {
            await axios.post(`${API}/admin/operators-by-phone`, { name, phone, password });
            toast.success(`Location "${name}" created`);
            setForm({ name: '', phone: '', password: '' });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create location');
        } finally {
            setCreating(false);
        }
    };

    const openFeatures = async (op) => {
        try {
            const { data } = await axios.get(`${API}/admin/operators/${op.id}/features`);
            setEditing(data);
        } catch (e) {
            toast.error('Failed to load feature flags');
        }
    };

    const toggleFeature = (k) => {
        setEditing(prev => ({ ...prev, features: { ...prev.features, [k]: !prev.features[k] } }));
    };

    const saveFeatures = async () => {
        if (!editing) return;
        setBusy(true);
        try {
            await axios.put(`${API}/admin/operators/${editing.operator_id}/features`, { features: editing.features });
            toast.success(`Features updated for ${editing.operator_name}`);
            setEditing(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setBusy(false);
        }
    };

    const allOn = (features) => Object.values(features || {}).every(Boolean);
    const allOff = (features) => Object.values(features || {}).every(v => !v);
    const setAll = (val) => setEditing(prev => ({
        ...prev,
        features: Object.fromEntries(Object.keys(prev.features).map(k => [k, val]))
    }));

    const filtered = operators.filter(o => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return (o.name || '').toLowerCase().includes(q) || (o.phone || '').includes(q) || (o.email || '').toLowerCase().includes(q);
    });

    const exportCsv = () => {
        csv(`locations_${new Date().toISOString().slice(0,10)}.csv`, [
            ['Name', 'Phone', 'Email', 'Players', 'Status', 'Created'],
            ...filtered.map(o => [o.name, o.phone, o.email, o.player_count || 0, o.status || 'active', fmtDate(o.created_at)]),
        ]);
        toast.success(`Exported ${filtered.length} location(s)`);
    };

    return (
        <div className="space-y-6" data-testid="staff-admin-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#FFD700]" />
                    Locations
                    <span className="text-xs text-[#A0AAB2] font-normal">({operators.length})</span>
                </h3>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={exportCsv}
                        className="border-white/20 text-white h-8 text-[11px]"
                        data-testid="staff-export">
                        <Download className="w-3.5 h-3.5 mr-1" />CSV
                    </Button>
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}
                        className="border-white/20 text-white h-8 text-[11px]"
                        data-testid="staff-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Create Location */}
                <div className="card-dark p-5 lg:col-span-1" style={{ borderLeft: '3px solid #FFD700' }}>
                    <div className="flex items-center gap-2 mb-4">
                        <UserPlus className="w-4 h-4 text-[#FFD700]" />
                        <h4 className="text-sm font-bold text-white">Add Location</h4>
                    </div>
                    <p className="text-xs text-[#A0AAB2] mb-3">
                        Each location is a separate operator. They only see their own players + cashiers.
                    </p>
                    <form onSubmit={submitOperator} className="space-y-3">
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2]">Location name</Label>
                            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. Downtown Store"
                                className="mt-1 bg-[#121212] border-white/10 text-white h-9"
                                data-testid="staff-op-name" />
                        </div>
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2]">Owner phone</Label>
                            <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                                placeholder="3015551234"
                                className="mt-1 bg-[#121212] border-white/10 text-white h-9"
                                data-testid="staff-op-phone" />
                        </div>
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2]">Password (they'll log in with phone + this)</Label>
                            <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                className="mt-1 bg-[#121212] border-white/10 text-white h-9"
                                data-testid="staff-op-password" />
                        </div>
                        <Button type="submit" disabled={creating}
                            className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold"
                            data-testid="staff-op-create">
                            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-2" />Create Location</>}
                        </Button>
                    </form>
                </div>

                {/* Locations list */}
                <div className="card-dark p-5 lg:col-span-2">
                    <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0AAB2]" />
                        <Input value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Search by name, phone or email…"
                            className="pl-10 bg-[#121212] border-white/10 text-white h-9"
                            data-testid="staff-search" />
                    </div>
                    {loading ? (
                        <div className="text-center py-10 text-[#A0AAB2]">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-10 text-[#A0AAB2]">
                            {search ? 'No locations match that search.' : 'No locations yet. Create your first one →'}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-white/10">
                                        <TableHead className="text-[#A0AAB2]">Name</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Phone</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Players</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Status</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Features</TableHead>
                                        <TableHead className="text-[#A0AAB2]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((op, i) => (
                                        <TableRow key={op.id} className="border-white/5" data-testid={`staff-op-row-${i}`}>
                                            <TableCell className="text-white font-bold">{op.name}</TableCell>
                                            <TableCell className="text-[#A0AAB2] font-mono text-sm">{op.phone || '—'}</TableCell>
                                            <TableCell className="text-right text-white">{op.player_count || 0}</TableCell>
                                            <TableCell>
                                                <span className={`text-xs font-bold ${op.status === 'active' ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                    {op.status || 'active'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <Button size="sm" variant="outline"
                                                    onClick={() => openFeatures(op)}
                                                    className="border-[#00E0FF]/30 text-[#00E0FF] hover:bg-[#00E0FF]/10 h-7 text-[11px]"
                                                    data-testid={`features-btn-${i}`}>
                                                    <Settings className="w-3.5 h-3.5 mr-1" />Edit features
                                                </Button>
                                            </TableCell>
                                            <TableCell className="text-right text-[10px] text-[#666]">
                                                {fmtDate(op.created_at)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </div>

            {/* Feature toggles dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-2xl" data-testid="features-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Settings className="w-5 h-5 text-[#00E0FF]" /> Features — {editing?.operator_name}
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Turn each feature ON or OFF for this location. Players that belong to this operator will only see what's enabled.
                        </DialogDescription>
                    </DialogHeader>

                    {editing && (
                        <>
                            <div className="flex items-center gap-2 mb-3">
                                <Button size="sm" variant="outline" onClick={() => setAll(true)} disabled={allOn(editing.features)}
                                    className="border-[#39FF14]/30 text-[#39FF14] h-7 text-[11px]" data-testid="features-all-on">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />All ON
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setAll(false)} disabled={allOff(editing.features)}
                                    className="border-[#FF3B30]/30 text-[#FF3B30] h-7 text-[11px]" data-testid="features-all-off">
                                    <X className="w-3 h-3 mr-1" />All OFF
                                </Button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[50vh] overflow-y-auto pr-2">
                                {editing.catalog.map(f => (
                                    <div key={f.key}
                                        className={`flex items-start gap-3 p-3 rounded-md border transition-colors ${
                                            editing.features[f.key]
                                                ? 'border-[#39FF14]/30 bg-[#39FF14]/5'
                                                : 'border-white/10 bg-[#0a0a0a]'
                                        }`}
                                        data-testid={`feature-row-${f.key}`}>
                                        <Switch
                                            checked={!!editing.features[f.key]}
                                            onCheckedChange={() => toggleFeature(f.key)}
                                            data-testid={`feature-toggle-${f.key}`}
                                        />
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-white">{f.label}</p>
                                            <p className="text-[10px] text-[#A0AAB2]">{f.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)} className="border-white/10 text-white">Cancel</Button>
                        <Button onClick={saveFeatures} disabled={busy} className="bg-[#39FF14] text-black font-bold" data-testid="features-save">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
